import { ApiSchemas } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';

import { buildMetricStatus, MetricStatus } from './metric-status';

// Raw metric dictionary shape coming from the API
type ExternalCallMetric = ApiSchemas['ExternalCallMetric'];

interface MetricRowCellProps {
  metric: ExternalCallMetric | undefined;
}

export function MetricRowCell({ metric }: MetricRowCellProps) {
  const status = buildMetricStatus(metric);

  if (status.isEmpty) return <span>—</span>;

  switch (status.kind) {
    case 'boolean':
      return <MetricRowBoolean status={status} />;
    case 'number':
      return <MetricRowNumber status={status} />;
    case 'string':
      return <MetricRowString status={status} />;
    case 'list':
      return <MetricRowList status={status} />;
    default:
      return <span>—</span>;
  }
}

interface MetricRowBooleanProps {
  status: MetricStatus & { kind: 'boolean' };
}

function MetricRowBoolean({ status }: MetricRowBooleanProps) {
  return status.truthy ? <Badge variant='secondary'>Yes</Badge> : <Badge variant='secondary'>No</Badge>;
}

interface MetricRowNumberProps {
  status: MetricStatus & { kind: 'number' };
}

function MetricRowNumber({ status }: MetricRowNumberProps) {
  return (
    <div className='flex items-center'>
      <div className='mr-2 tabular-nums'>{(status.value ?? 0)}</div>
      <div className='h-2 flex-1 rounded bg-gray-200 dark:bg-gray-700'>
        <div
          className={status.barColor + ' h-2 rounded'}
          style={{ width: `${Math.min(Math.max(status.percent || 0, 0), 100)}%` }}
        ></div>
      </div>
    </div>
  );
}

interface MetricRowStringProps {
  status: MetricStatus & { kind: 'string' };
}

function MetricRowString({ status }: MetricRowStringProps) {
  return <span className='max-w-[10rem] truncate'>{status.value}</span>;
}

interface MetricRowListProps {
  status: MetricStatus & { kind: 'list' };
}

function MetricRowList({ status }: MetricRowListProps) {
  if (status.value.length === 0) {
    return <span className='text-muted-foreground'>None</span>;
  }

  if (status.value.length <= 2) {
    return (
      <div className='flex gap-1'>
        {status.value.map((t: string) => (
          <span key={t} className='bg-muted rounded px-2 py-2 text-sm text-[10px] font-normal'>
            {t}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className='flex gap-1'>
      {status.value.map((t: string) => (
        <Badge key={t} variant='outline' className='px-1 py-0 text-[10px] font-normal'>
          {t}
        </Badge>
      ))}
    </div>
  );
}
